import { ArrowDown, ArrowUp, ArrowUpDown, ChevronLeft, ChevronRight, Filter } from "lucide-react";
import {
  Children,
  isValidElement,
  type ReactNode,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import { useReadableWidth } from "../hooks/use-readable-width";
import type { TableControls } from "../hooks/use-table-controls";
import { reviewContentForDisplay } from "../lib/review-display";
import { Button, Input, stateLabel } from "./primitives";
import { Disclosure, useWorkspaceActive } from "./shared-ui";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import { Tooltip, TooltipContent, TooltipTrigger } from "./ui/tooltip";

export const catalogueStateChoices = ["DRAFT", "ACTIVE", "INACTIVE", "ARCHIVED"].map((value) => ({
  value,
  label: value.charAt(0) + value.slice(1).toLowerCase(),
}));

type SortDirection = "asc" | "desc";

function cellText(node: ReactNode): string {
  return Children.toArray(node)
    .map((child) => {
      if (!isValidElement<{ children?: ReactNode; value?: string | number }>(child))
        return String(child);
      return child.props.children
        ? cellText(child.props.children)
        : String(child.props.value ?? "");
    })
    .join(" ");
}

function ColumnHeader({
  children,
  label,
  direction,
  onSort,
  value,
  onFilter,
  filter,
  filterActive,
  filterChoices,
  filterKind,
}: {
  children: ReactNode;
  label: string;
  direction: SortDirection | undefined;
  onSort: (() => void) | undefined;
  value: string;
  onFilter: ((value: string) => void) | undefined;
  filter: ReactNode;
  filterActive: boolean;
  filterChoices?: ReadonlyArray<{ value: string; label: string }> | undefined;
  filterKind: "text" | "choice" | "date" | "none";
}) {
  const active = useWorkspaceActive();
  useEffect(() => {
    if (!active) setOpen(false);
  }, [active]);
  const [open, setOpen] = useState(false),
    [draft, setDraft] = useState(value);
  const id = useId();
  useEffect(() => setDraft(value), [value]);
  return (
    <div className="cyra-column-control">
      {onSort ? (
        <SortableHeader direction={direction} onSort={onSort}>
          {children}
        </SortableHeader>
      ) : (
        children
      )}
      {onFilter || filter ? (
        <Popover open={open && active} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              size="small"
              variant="ghost"
              aria-label={`Filter ${label}`}
              aria-pressed={filterActive || Boolean(value)}
            >
              <Filter
                aria-hidden
                size={13}
                fill={filterActive || value ? "currentColor" : "none"}
              />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            align="start"
            className="cyra-column-filter w-64"
            aria-label={`Filter ${label}`}
          >
            {filter ??
              (filterChoices ? (
                <div className="space-y-2">
                  <label htmlFor={id} className="block text-sm font-medium">
                    {label}
                  </label>
                  <Select
                    value={value || "__all__"}
                    onValueChange={(selected) => {
                      onFilter?.(selected === "__all__" ? "" : selected);
                      setOpen(false);
                    }}
                  >
                    <SelectTrigger id={id} aria-label={`${label} filter`} className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__all__">All</SelectItem>
                      {filterChoices.map((choice) => (
                        <SelectItem key={choice.value} value={choice.value}>
                          {choice.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                <form
                  onSubmit={(event) => {
                    event.preventDefault();
                    onFilter?.(draft);
                    setOpen(false);
                  }}
                >
                  <label className="mb-2 block text-sm font-medium" htmlFor={id}>
                    {filterKind === "date" ? label : `Search ${label.toLowerCase()}`}
                  </label>
                  <Input
                    id={id}
                    maxLength={100}
                    value={draft}
                    onChange={(event) => setDraft(event.target.value)}
                    type={filterKind === "date" ? "date" : "search"}
                    placeholder={filterKind === "date" ? undefined : "Type to search"}
                  />
                  <div className="mt-3 flex justify-between gap-2">
                    <Button
                      size="small"
                      variant="ghost"
                      onClick={() => {
                        setDraft("");
                        onFilter?.("");
                        setOpen(false);
                      }}
                    >
                      Clear
                    </Button>
                    <Button size="small" variant="primary" type="submit">
                      Apply
                    </Button>
                  </div>
                </form>
              ))}
          </PopoverContent>
        </Popover>
      ) : null}
    </div>
  );
}

export interface DataTableColumn<Row> {
  value?: ((row: Row) => string | number | null) | undefined;
  filter?: ReactNode;
  filterKind?: "text" | "choice" | "date" | "none";
  sortable?: boolean;
  filterActive?: boolean;
  filterChoices?: ReadonlyArray<{ value: string; label: string }>;
  compact?: "identity" | "status" | "action" | "summary" | "detail";
  dataLabel: string;
  header: ReactNode;
  id: string;
  onSort?: (() => void) | undefined;
  render: (row: Row) => ReactNode;
  sortDirection?: SortDirection | undefined;
}

interface DataTableProps<Row> {
  controls?: TableControls | undefined;
  filterOptions?: Record<string, string[]> | undefined;
  caption: string;
  columns: Array<DataTableColumn<Row>>;
  footer?: ReactNode;
  getRowKey: (row: Row) => string;
  pageSize?: number;
  rows: Row[];
  forceTable?: boolean;
  onRowOpen?: ((row: Row) => void) | undefined;
}

export function SortableHeader({
  children,
  direction,
  onSort,
}: {
  children: ReactNode;
  direction?: SortDirection | undefined;
  onSort: () => void;
}) {
  const Icon = direction === "asc" ? ArrowUp : direction === "desc" ? ArrowDown : ArrowUpDown;
  const directionLabel =
    direction === "asc" ? "ascending" : direction === "desc" ? "descending" : "";
  const label = `Sort by ${String(children)}${directionLabel ? `, currently ${directionLabel}` : ""}`;
  return (
    <Button aria-label={label} className="-ml-2" onClick={onSort} size="small" variant="ghost">
      {children}
      <Icon aria-hidden="true" />
    </Button>
  );
}

export function DataTable<Row>({
  caption,
  columns: sourceColumns,
  controls,
  filterOptions,
  footer,
  getRowKey,
  pageSize = 5,
  rows,
  forceTable = false,
  onRowOpen,
}: DataTableProps<Row>) {
  const recordHost = useRef<HTMLDivElement>(null);
  const active = useWorkspaceActive();
  const readableCompact = useReadableWidth(recordHost, 48, active);
  const compact = readableCompact && !forceTable;
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(pageSize);
  const [localSort, setLocalSort] = useState<{ id: string; direction: SortDirection } | null>(null);
  const [localFilters, setLocalFilters] = useState<Record<string, string>>({});
  const local = footer === undefined && !controls;
  const getValue = (row: Row, column: DataTableColumn<Row>) =>
    column.value ? column.value(row) : cellText(column.render(row));
  const filterKind = (column: DataTableColumn<Row>) =>
    column.filterKind ?? (column.filterChoices ? "choice" : "text");
  const matches = (row: Row, column: DataTableColumn<Row>, selected: string) => {
    if (!selected) return true;
    const raw = getValue(row, column);
    const value =
      filterKind(column) === "date" && typeof raw === "number"
        ? new Date(raw).toISOString().slice(0, 10)
        : String(raw ?? "");
    return filterKind(column) === "choice"
      ? value.toLowerCase() === selected.toLowerCase()
      : value.toLowerCase().includes(selected.toLowerCase());
  };
  const selectedRows = local
    ? rows.filter((row) =>
        sourceColumns.every((column) => matches(row, column, localFilters[column.id] ?? "")),
      )
    : rows;
  const sortedRows =
    local && localSort
      ? [...selectedRows].sort((left, right) => {
          const column = sourceColumns.find((item) => item.id === localSort.id)!;
          const a = getValue(left, column),
            b = getValue(right, column);
          const comparison =
            typeof a === "number" && typeof b === "number"
              ? a - b
              : String(a ?? "").localeCompare(String(b ?? ""), undefined, { numeric: true });
          return localSort.direction === "asc" ? comparison : -comparison;
        })
      : selectedRows;
  const columns = sourceColumns.map((column) => {
    if (column.compact === "action") return column;
    const sortDirection = controls
      ? controls.query.tableSort === column.id
        ? controls.query.tableDirection
        : undefined
      : localSort?.id === column.id
        ? localSort.direction
        : column.sortDirection;
    const onSort =
      column.sortable === false
        ? undefined
        : controls
          ? () => controls.setSort(column.id)
          : (column.onSort ??
            (local
              ? () =>
                  setLocalSort({
                    id: column.id,
                    direction:
                      localSort?.id === column.id && localSort.direction === "asc" ? "desc" : "asc",
                  })
              : undefined));
    const filterValue = controls?.filters[column.id] ?? localFilters[column.id] ?? "";
    const setFilter = (value: string) => {
      if (controls) controls.setFilter(column.id, value);
      else {
        setLocalFilters((current) => ({ ...current, [column.id]: value }));
        setCurrentPage(1);
      }
    };
    const kind = filterKind(column);
    const candidates =
      kind === "choice"
        ? (filterOptions?.[column.id] ?? [
            ...new Set(
              rows
                .filter(
                  (row) =>
                    !local ||
                    sourceColumns.every(
                      (other) =>
                        other.id === column.id || matches(row, other, localFilters[other.id] ?? ""),
                    ),
                )
                .map((row) => String(getValue(row, column) ?? ""))
                .filter(Boolean),
            ),
          ])
        : undefined;
    const choices = candidates?.map((value) => ({
      value,
      label:
        column.filterChoices?.find((item) => item.value === value)?.label ??
        (/^[A-Z0-9_]+$/.test(value) ? stateLabel(value) : value),
    }));
    return {
      ...column,
      onSort,
      sortDirection,
      header: (
        <ColumnHeader
          label={column.dataLabel}
          direction={sortDirection}
          onSort={onSort}
          value={filterValue}
          onFilter={
            kind !== "none" &&
            (kind !== "choice" || Boolean(choices?.length) || Boolean(filterValue)) &&
            (controls || local)
              ? setFilter
              : undefined
          }
          filter={column.filter}
          filterChoices={choices}
          filterKind={kind}
          filterActive={column.filterActive ?? false}
        >
          {column.header}
        </ColumnHeader>
      ),
    };
  });
  const pageCount = Math.max(1, Math.ceil(sortedRows.length / rowsPerPage));
  const clientPaginated = footer === undefined;

  useEffect(() => {
    setCurrentPage((page) => Math.min(page, pageCount));
  }, [pageCount]);

  const visibleRows = clientPaginated
    ? sortedRows.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage)
    : sortedRows;
  const resolvedFooter =
    footer ??
    (rows.length ? (
      <Pagination
        currentPage={currentPage}
        onPageChange={setCurrentPage}
        onPageSizeChange={(nextPageSize) => {
          setRowsPerPage(nextPageSize);
          setCurrentPage(1);
        }}
        pageCount={pageCount}
        pageSize={rowsPerPage}
        totalItems={sortedRows.length}
      />
    ) : null);

  return (
    <div
      ref={recordHost}
      data-record-collection={compact ? "compact" : "table"}
      className="min-w-0 overflow-hidden rounded-lg border bg-card"
    >
      {compact ? (
        <CompactRecords
          caption={caption}
          columns={columns}
          getRowKey={getRowKey}
          rows={visibleRows}
        />
      ) : (
        <Table>
          <TableCaption className="sr-only">{caption}</TableCaption>
          <TableHeader>
            <TableRow>
              {columns.map((column) => (
                <TableHead
                  className="h-8"
                  aria-sort={
                    column.onSort
                      ? column.sortDirection === "asc"
                        ? "ascending"
                        : column.sortDirection === "desc"
                          ? "descending"
                          : "none"
                      : undefined
                  }
                  key={column.id}
                  scope="col"
                >
                  {column.header}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {visibleRows.map((row) => (
              <TableRow
                key={getRowKey(row)}
                id={`record-${getRowKey(row)}`}
                tabIndex={onRowOpen ? 0 : undefined}
                className={onRowOpen ? "cursor-pointer" : undefined}
                onClick={(event) => {
                  if (
                    !onRowOpen ||
                    !event.currentTarget.contains(event.target as Node) ||
                    event.button !== 0 ||
                    event.ctrlKey ||
                    event.metaKey ||
                    event.shiftKey ||
                    event.altKey
                  )
                    return;
                  if (
                    (event.target as HTMLElement).closest(
                      "a,button,input,select,textarea,[role=button],[role=combobox],[role=menuitem]",
                    )
                  )
                    return;
                  onRowOpen(row);
                }}
                onKeyDown={(event) => {
                  if (event.target === event.currentTarget && event.key === "Enter") {
                    event.preventDefault();
                    onRowOpen?.(row);
                  }
                }}
              >
                {columns.map((column) => (
                  <TableCell
                    className="py-1.5 whitespace-normal wrap-anywhere [&_a[data-slot=button]]:min-h-7 [&_a[data-slot=button]]:px-2 [&_a[data-slot=button]]:py-1"
                    data-label={column.dataLabel}
                    key={column.id}
                  >
                    {reviewContentForDisplay(column.render(row))}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      {visibleRows.length === 0 ? (
        <p className="p-5 text-sm text-muted-foreground" role="status">
          No records match.
        </p>
      ) : null}
      {resolvedFooter}
    </div>
  );
}

function CompactRecords<Row>({
  caption,
  columns,
  getRowKey,
  rows,
}: Pick<DataTableProps<Row>, "caption" | "columns" | "getRowKey" | "rows">) {
  const identity = columns.find((column) => column.compact === "identity") ?? columns[0];
  const controls = columns.filter(
    (column) => column !== identity && (column.compact === "status" || column.compact === "action"),
  );
  const summary = columns.filter(
    (column) => column !== identity && !controls.includes(column) && column.compact !== "detail",
  );
  const details = columns.filter((column) => column !== identity && column.compact === "detail");
  const sortable = columns.filter((column) => column.onSort);
  const fields = (row: Row, selected: Array<DataTableColumn<Row>>) => (
    <dl
      className={`grid ${selected.length === 1 ? "grid-cols-1" : "grid-cols-2"} gap-x-3 gap-y-2 text-sm`}
    >
      {selected.map((column) => (
        <div key={column.id} className="min-w-0">
          <dt className="text-xs text-muted-foreground">{column.dataLabel}</dt>
          <dd className="min-w-0 wrap-anywhere">{reviewContentForDisplay(column.render(row))}</dd>
        </div>
      ))}
    </dl>
  );
  return (
    <>
      {sortable.length ? (
        <Disclosure title="Sort records">
          <div className="flex flex-wrap gap-2 px-3">
            {sortable.map((column) => (
              <div key={column.id}>{column.header}</div>
            ))}
          </div>
        </Disclosure>
      ) : null}
      <ul aria-label={caption} className="divide-y">
        {rows.map((row) => (
          <li key={getRowKey(row)} data-record-row className="space-y-2 py-3">
            <div data-record-part="identity" className="min-w-0 wrap-anywhere font-medium">
              {identity ? reviewContentForDisplay(identity.render(row)) : null}
            </div>
            {controls.length ? (
              <div className="flex flex-wrap items-center gap-2">
                {controls.map((column) => (
                  <div
                    key={column.id}
                    data-record-part={column.compact}
                    className="min-w-0 max-w-full wrap-anywhere"
                  >
                    {reviewContentForDisplay(column.render(row))}
                  </div>
                ))}
              </div>
            ) : null}
            {summary.length ? fields(row, summary) : null}
            {details.length ? (
              <Disclosure title="Details">{fields(row, details)}</Disclosure>
            ) : null}
          </li>
        ))}
      </ul>
    </>
  );
}

export function Pagination({
  currentPage,
  onPageChange,
  onPageSizeChange,
  pageCount,
  pageSizeOptions = [5, 10, 20, 50],
  pageSize,
  totalItems,
}: {
  currentPage: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: ((pageSize: number) => void) | undefined;
  pageCount: number;
  pageSizeOptions?: number[];
  pageSize?: number;
  totalItems?: number;
}) {
  const pageSizeLabelId = useId();
  const canCompact =
    onPageSizeChange &&
    pageSize !== undefined &&
    pageSizeOptions.some(
      (size) => size < pageSize && (totalItems === undefined || size < totalItems),
    );
  if (pageCount <= 1 && currentPage <= 1 && !canCompact) return null;
  const resolvedPageSizeOptions =
    pageSize === undefined
      ? pageSizeOptions
      : [...new Set([pageSize, ...pageSizeOptions])].sort((left, right) => left - right);
  const range =
    totalItems !== undefined && pageSize !== undefined
      ? `${Math.min((currentPage - 1) * pageSize + 1, totalItems)}–${Math.min(currentPage * pageSize, totalItems)} of ${totalItems} · `
      : "";
  return (
    <nav
      aria-label="Table pagination"
      className="flex flex-col items-start gap-2 border-t py-2 sm:flex-row sm:items-center sm:justify-between"
    >
      <span aria-live="polite" className="text-xs text-muted-foreground">
        {range}Page {currentPage} of {pageCount}
      </span>
      <div className="flex w-full items-center justify-between gap-3 sm:w-auto sm:justify-start">
        {pageSize !== undefined && onPageSizeChange ? (
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground" id={pageSizeLabelId}>
              Rows per page
            </span>
            <Select
              onValueChange={(value) => onPageSizeChange(Number(value))}
              value={String(pageSize)}
            >
              <SelectTrigger aria-labelledby={pageSizeLabelId} className="w-18" size="sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {resolvedPageSizeOptions.map((option) => (
                  <SelectItem key={option} value={String(option)}>
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ) : null}
        {pageCount > 1 || currentPage > 1 ? (
          <div className="flex items-center gap-1">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  aria-label="Go to previous page"
                  disabled={currentPage <= 1}
                  onClick={() => onPageChange(currentPage - 1)}
                  size="small"
                  variant="ghost"
                >
                  <ChevronLeft aria-hidden="true" />
                  <span className="sr-only">Previous page</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent>Previous page</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  aria-label="Go to next page"
                  disabled={currentPage >= pageCount}
                  onClick={() => onPageChange(currentPage + 1)}
                  size="small"
                  variant="ghost"
                >
                  <ChevronRight aria-hidden="true" />
                  <span className="sr-only">Next page</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent>Next page</TooltipContent>
            </Tooltip>
          </div>
        ) : null}
      </div>
    </nav>
  );
}
